import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function StartStore() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    category: '',
    tags: '',
    socialLinks: {
      instagram: '',
      twitter: '',
    },
    contact: {
      email: '',
      phone: '',
      address: {
        country: 'India',
      },
    },
    theme: {
      primaryColor: '#1E40AF',
      secondaryColor: '#F59E0B',
      fontFamily: 'Inter',
    },
  });

  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const keys = name.split('.');
      setFormData(prev => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: keys.length > 2 ? {
            ...prev[keys[0]][keys[1]],
            [keys[2]]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    const formDataToSend = new FormData();
    
    // Append simple fields
    formDataToSend.append('name', formData.name);
    formDataToSend.append('slug', formData.slug);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('category', formData.category);
    formDataToSend.append('tags', formData.tags);
    
    // Append nested objects as JSON strings
    formDataToSend.append('socialLinks', JSON.stringify(formData.socialLinks));
    formDataToSend.append('contact', JSON.stringify(formData.contact));
    formDataToSend.append('theme', JSON.stringify(formData.theme));

    // Append files if they exist
    if (logo) {
      formDataToSend.append('logo', logo);
    }
    if (banner) {
      formDataToSend.append('banner', banner);
    }

    // Log form data before sending (for debugging)
    console.log('FormData entries:');
    for (let [key, value] of formDataToSend.entries()) {
      console.log(key, value);
    }

    const response = await api.post('/stores', formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.success) {
      navigate(`/store/${response.data.data.store.slug}`);
    }
  } catch (err) {
    console.error('Error creating store:', err);
    
    // Handle different error formats
    if (err.response?.data?.errors) {
      // Validation errors from backend
      const errorMessages = err.response.data.errors.map(e => e.msg).join(', ');
      setError(errorMessages);
    } else if (err.response?.data?.message) {
      // Custom error message from backend
      setError(err.response.data.message);
    } else if (err.message) {
      // General error
      setError(err.message);
    } else {
      setError('Failed to create store. Please try again.');
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Start Your Store</h1>
          <p className="mt-2 text-sm text-gray-600">Fill out the form to create your store.</p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
          {/* Store Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Store Name *</label>
            <input 
              name="name" 
              type="text" 
              required 
              value={formData.name} 
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Slug *</label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                yourstore.com/
              </span>
              <input 
                name="slug" 
                type="text" 
                required 
                value={formData.slug} 
                onChange={handleChange}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                placeholder="store-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea 
              name="description" 
              rows="3" 
              value={formData.description} 
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
              placeholder="Tell customers about your store..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
              >
                <option value="">Select a category</option>
                <option value="fashion">Fashion</option>
                <option value="lifestyle">Lifestyle</option>
                <option value="art">Art</option>
                <option value="music">Music</option>
                <option value="sports">Sports</option>
                <option value="technology">Technology</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tags (comma separated)</label>
              <input 
                name="tags" 
                type="text" 
                value={formData.tags} 
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Social Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Instagram</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    instagram.com/
                  </span>
                  <input 
                    name="socialLinks.instagram" 
                    type="text" 
                    value={formData.socialLinks.instagram} 
                    onChange={handleChange}
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                    placeholder="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Twitter</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    twitter.com/
                  </span>
                  <input 
                    name="socialLinks.twitter" 
                    type="text" 
                    value={formData.socialLinks.twitter} 
                    onChange={handleChange}
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                    placeholder="username"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input 
                  name="contact.email" 
                  type="email" 
                  required
                  value={formData.contact.email} 
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                  placeholder="contact@yourstore.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input 
                  name="contact.phone" 
                  type="tel" 
                  value={formData.contact.phone} 
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                  placeholder="+91 9876543210"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <input 
                  name="contact.address.country" 
                  type="text" 
                  value={formData.contact.address.country} 
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                />
              </div>
            </div>
          </div>

          {/* Theme */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Store Theme</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Primary Color</label>
                <div className="mt-1 flex items-center">
                  <input 
                    name="theme.primaryColor" 
                    type="color" 
                    value={formData.theme.primaryColor} 
                    onChange={handleChange}
                    className="h-10 w-10 rounded border border-gray-300 cursor-pointer" 
                  />
                  <span className="ml-2 text-sm text-gray-500">{formData.theme.primaryColor}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Secondary Color</label>
                <div className="mt-1 flex items-center">
                  <input 
                    name="theme.secondaryColor" 
                    type="color" 
                    value={formData.theme.secondaryColor} 
                    onChange={handleChange}
                    className="h-10 w-10 rounded border border-gray-300 cursor-pointer" 
                  />
                  <span className="ml-2 text-sm text-gray-500">{formData.theme.secondaryColor}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Font Family</label>
                <select
                  name="theme.fontFamily"
                  value={formData.theme.fontFamily}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Montserrat">Montserrat</option>
                </select>
              </div>
            </div>
          </div>

          {/* File Uploads */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Store Images</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Logo</label>
                <div className="mt-1 flex items-center">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => setLogo(e.target.files[0])}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100" 
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Square image recommended (500x500px)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Banner</label>
                <div className="mt-1 flex items-center">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => setBanner(e.target.files[0])}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100" 
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Wide image recommended (1500x500px)</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-6 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : 'Create Store'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}